import React from "react";

/**
 * The multi-angle track set, and a way to move between its sessions.
 *
 * MA-3, 2026-09-09. **This replaces a banner that lied.** Wave 2b rendered
 * "Angle 1 of 3" from a React state array and never advanced past the first —
 * `setCurrentAngleIdx` was only ever called with `0`. It claimed a multi-angle
 * run the shell did not perform, and it was mine.
 *
 * A track is a real creative session now (MA-1), so moving between tracks is
 * navigation, not local state: each tab opens that session at whatever step it
 * has reached. Nothing is lost on reload, because there was never anything in
 * memory to lose.
 *
 * WHAT IT SHOWS AND WHY:
 *   · the angle's name, because "track 2" means nothing on its own;
 *   · each track's own step, because they progress independently — that is the
 *     entire point of the Founder's ruling, and a set where two tracks are on
 *     different steps is correct, not a bug to hide;
 *   · `completed` where a track has finished, because ungated submission means
 *     one track can be done while its siblings are still running.
 *
 * A SET OF ONE RENDERS NOTHING. A session that is simply a session should not
 * grow furniture explaining that it is one of one.
 */
export default function TrackSwitcher({ tracks, currentSessionId, onOpen }) {
  if (!tracks || tracks.state === "loading") return null;

  // A FAILED READ IS NOT AN ABSENT SET (rule 10). The shell cannot silently
  // drop the one control that reaches a track's siblings — an operator who
  // cannot see track 3 would reasonably conclude it was never created.
  if (tracks.state === "error") {
    return (
      <p className="ws-step-note state-error" role="alert"
         data-testid="ws-tracks-failed">
        This session's angle tracks could not be read: {tracks.error}. If this
        session is part of a multi-angle set, its siblings are still there —
        they are creative sessions in their own right and appear in the session
        list.
      </p>
    );
  }

  const rows = tracks.data?.tracks || [];
  if (rows.length < 2) return null;

  const total = tracks.data?.track_total ?? rows.length;

  return (
    <div className="ws-tracks" data-testid="ws-tracks">
      <p className="dim ws-tracks-head">
        This offer is being worked from {total} angles. Each is its own session
        and finishes on its own.
      </p>
      <ol className="ws-tracks-list">
        {rows.map((t) => {
          const here = t.session_id === currentSessionId;
          return (
            <li key={t.session_id}>
              <button
                type="button"
                className={`ws-track ${here ? "is-current" : ""}`}
                onClick={() => (here ? undefined : onOpen(t.session_id))}
                aria-current={here ? "true" : undefined}
                disabled={here}
                data-testid={`ws-track-${t.track_index}`}
              >
                <span className="ws-track-n">Track {t.track_index}</span>
                <span className="ws-track-angle">
                  {t.angle_name || <span className="dim">angle unnamed</span>}
                </span>
                <span className="dim ws-track-step">
                  {t.status === "completed"
                    ? "complete"
                    : t.step_label || `step ${(t.current_step ?? 0) + 1}`}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
